import * as React from 'react';
import { Link, useParams } from 'react-router';
import { useMutation } from '@tanstack/react-query';
import {
  CheckCircle2Icon,
  QrCodeIcon,
  TrophyIcon,
  ArrowLeftIcon,
  SparklesIcon,
  AlertTriangleIcon,
  CameraIcon,
  MapPinIcon,
  FileTextIcon,
  HelpCircleIcon,
  RotateCcwIcon,
  PartyPopperIcon,
  XCircleIcon
} from 'lucide-react';

import {
  useGetHunt,
  useJoinHunt,
  captureStation,
  ApiError
} from '@cdf/api-client-react';
import type {
  HuntState,
  CaptureResult,
  PublicChallenge,
  PublicChallengeType
} from '@cdf/api-client-react';

import { useHuntSession, huntAuthHeaders, stashPendingCheckin } from '@/hooks/use-hunt-session';
import { useHuntChallenges } from '@/hooks/use-hunt-challenges';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';


// ── Confetti celebration ───────────────────────────────────────────────────

const CONFETTI_COLORS = ['#f59e0b', '#3b82f6', '#10b981', '#ef4444', '#8b5cf6', '#ec4899'];

export function Confetti() {
  const pieces = React.useMemo(
    () =>
      Array.from({ length: 50 }, (_, i) => ({
        id: i,
        left: Math.random() * 100,
        delay: Math.random() * 0.6,
        duration: 1.2 + Math.random() * 1.8,
        color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
        size: 8 + Math.random() * 10,
        rotation: Math.random() * 360,
        round: i % 3 === 0
      })),
    []
  );

  return (
    <>
      <style>{`
        @keyframes confetti-fall {
          0% { transform: translateY(-20px) rotate(0deg); opacity: 1; }
          85% { opacity: 1; }
          100% { transform: translateY(105vh) rotate(720deg); opacity: 0; }
        }
      `}</style>
      <div className="pointer-events-none fixed inset-0 z-50 overflow-hidden">
        {pieces.map((p) => (
          <div
            key={p.id}
            className="absolute top-0"
            style={{
              left: `${p.left}%`,
              width: `${p.size}px`,
              height: `${p.size}px`,
              backgroundColor: p.color,
              animation: `confetti-fall ${p.duration}s ${p.delay}s ease-out forwards`,
              transform: `rotate(${p.rotation}deg)`,
              borderRadius: p.round ? '50%' : '2px'
            }}
          />
        ))}
      </div>
    </>
  );
}

// ── Challenge type metadata ────────────────────────────────────────────────

const CHALLENGE_META: Record<
  PublicChallengeType,
  { label: string; icon: typeof CameraIcon; href: string }
> = {
  demo_report: { label: 'Demo Report', icon: FileTextIcon, href: '/report' },
  ar_view: { label: 'AR View', icon: CameraIcon, href: '/ar' },
  map_find: { label: 'Map Find', icon: MapPinIcon, href: '/map' },
  quiz: { label: 'Quiz', icon: HelpCircleIcon, href: '#' },
  cv_scan: { label: 'CV Scan', icon: CameraIcon, href: '/ar' },
};

export function ChallengeCard({
  challenge,
  slug
}: {
  challenge: PublicChallenge;
  slug: string;
}) {
  const meta = CHALLENGE_META[challenge.type];
  const Icon = meta.icon;
  const [selectedAnswer, setSelectedAnswer] = React.useState<number | null>(null);
  const [answeredCorrectly, setAnsweredCorrectly] = React.useState(false);
  const { completeChallenge, isCompleting } = useHuntChallenges();

  const quizConfig = challenge.config;
  const isQuiz = challenge.type === 'quiz';
  const hasQuizData = isQuiz && !!quizConfig?.question && !!quizConfig?.options && quizConfig.options.length > 0;

  const handleQuizAnswer = (index: number) => {
    if (answeredCorrectly || isCompleting) return;
    setSelectedAnswer(index);
    if (quizConfig?.correctIndex === index) {
      setAnsweredCorrectly(true);
      completeChallenge(challenge.id, { selectedAnswer: index });
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <Icon className="h-4 w-4 text-muted-foreground" />
            {challenge.title}
          </CardTitle>
          <Badge variant="secondary">+{challenge.points} pts</Badge>
        </div>
        <CardDescription>{meta.label} Challenge</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {challenge.instructions && (
          <p className="text-sm text-muted-foreground">{challenge.instructions}</p>
        )}

        {isQuiz && hasQuizData ? (
          <div className="space-y-2">
            <p className="text-sm font-medium">{quizConfig!.question}</p>
            <div className="space-y-2">
              {quizConfig!.options!.map((option, index) => {
                const isSelected = selectedAnswer === index;
                const isCorrect = quizConfig!.correctIndex === index;
                return (
                  <button
                    key={index}
                    type="button"
                    onClick={() => handleQuizAnswer(index)}
                    disabled={answeredCorrectly || isCompleting}
                    className={cn(
                      'flex w-full items-center gap-2 rounded-lg border p-3 text-left text-sm transition-colors',
                      answeredCorrectly && isCorrect && 'border-green-500 bg-green-500/10',
                      isSelected && !isCorrect && 'border-red-500 bg-red-500/10',
                      !isSelected && !answeredCorrectly && 'hover:bg-muted',
                      answeredCorrectly && !isCorrect && 'opacity-50'
                    )}
                  >
                    {option}
                    {answeredCorrectly && isCorrect && (
                      <CheckCircle2Icon className="ml-auto h-4 w-4 text-green-500" />
                    )}
                    {isSelected && !isCorrect && (
                      <XCircleIcon className="ml-auto h-4 w-4 text-red-500" />
                    )}
                  </button>
                );
              })}
            </div>
            {selectedAnswer !== null && !answeredCorrectly && (
              <p className="text-xs text-muted-foreground">Not quite — try again!</p>
            )}
            {answeredCorrectly && (
              <p className="text-xs font-medium text-green-600">
                Correct! +{challenge.points} pts awarded.
              </p>
            )}
          </div>
        ) : isQuiz ? (
          <Badge variant="outline">Quiz not configured</Badge>
        ) : (
          <Button asChild size="sm">
            <Link to={meta.href}>Start challenge</Link>
          </Button>
        )}

        <Button asChild variant="ghost" size="sm">
          <Link to={`/hunt/${slug}`}>
            <ArrowLeftIcon className="mr-1 h-3 w-3" />
            Passport
          </Link>
        </Button>
      </CardContent>
    </Card>
  );
}

// ── Inline join form (for cold-scan visitors) ─────────────────────────────

function InlineJoinForm({
  slug,
  huntName,
  onJoined
}: {
  slug: string;
  huntName: string;
  onJoined: () => void;
}) {
  const [nickname, setNickname] = React.useState('');
  const [team, setTeam] = React.useState('');
  const { saveSession } = useHuntSession(slug);

  const joinMutation = useJoinHunt({
    mutation: {
      onSuccess: (data) => {
        saveSession({
          huntSlug: slug,
          participantId: data.participantId,
          token: data.token,
          nickname: data.nickname,
          team: data.team ?? null
        });
        onJoined();
      }
    }
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = nickname.trim();
    if (trimmed.length < 1) return;
    joinMutation.mutate({ slug, data: { nickname: trimmed, team: team.trim() || undefined } });
  };

  const apiErr =
    joinMutation.error instanceof ApiError ? joinMutation.error : undefined;

  return (
    <div className="mx-auto flex min-h-[100dvh] max-w-md flex-col justify-center p-6">
      <Card>
        <CardHeader className="text-center">
          <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
            <QrCodeIcon className="h-6 w-6 text-primary" />
          </div>
          <CardTitle className="text-xl">Join {huntName}</CardTitle>
          <CardDescription>
            You scanned a station QR code! Enter a nickname to join the hunt and
            capture this station.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="nickname">Nickname</Label>
              <Input
                id="nickname"
                value={nickname}
                onChange={(e) => setNickname(e.target.value.slice(0, 30))}
                placeholder="Your nickname"
                maxLength={30}
                autoFocus
                disabled={joinMutation.isPending}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="team">Team (optional)</Label>
              <Input
                id="team"
                value={team}
                onChange={(e) => setTeam(e.target.value.slice(0, 30))}
                placeholder="Team name"
                maxLength={30}
                disabled={joinMutation.isPending}
              />
            </div>
            {apiErr && (
              <p className="text-sm text-destructive">
                {apiErr.status === 409
                  ? 'This hunt is not currently active.'
                  : apiErr.message}
              </p>
            )}
            <Button
              type="submit"
              className="w-full"
              disabled={joinMutation.isPending || nickname.trim().length < 1}
            >
              {joinMutation.isPending ? 'Joining…' : 'Join & capture'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

// ── Capture result states ──────────────────────────────────────────────────

type CapturePhase =
  | { kind: 'idle' }
  | { kind: 'success'; result: CaptureResult }
  | { kind: 'already'; stationId: string; stationName: string }
  | { kind: 'invalid' }
  | { kind: 'expired' }
  | { kind: 'error'; message: string };

function CaptureCelebration({
  result,
  hunt,
  slug
}: {
  result: CaptureResult;
  hunt: HuntState;
  slug: string;
}) {
  const challenges = hunt.challenges.filter((c) => c.stationId === result.stationId);

  return (
    <>
      <Confetti />
      <div className="mx-auto flex min-h-[100dvh] max-w-md flex-col items-center justify-center p-6">
        <div className="mb-6 flex flex-col items-center text-center">
          <div className="relative mb-4">
            <div className="flex h-24 w-24 items-center justify-center rounded-full bg-green-500/10">
              <PartyPopperIcon className="h-12 w-12 text-green-600 dark:text-green-400" />
            </div>
          </div>
          <h1 className="text-2xl font-bold tracking-tight">Station Captured!</h1>
          <p className="mt-1 text-lg font-medium">{result.stationName}</p>
          <div className="mt-3 flex items-center gap-2">
            <Badge className="bg-green-600 hover:bg-green-600">
              <SparklesIcon className="mr-1 h-3 w-3" />
              +{result.pointsAwarded} points
            </Badge>
            <Badge variant="outline">
              <TrophyIcon className="mr-1 h-3 w-3" />
              {result.totalPoints} total
            </Badge>
          </div>
        </div>

        {challenges.length > 0 && (
          <div className="w-full space-y-3">
            <p className="text-center text-sm text-muted-foreground">
              Complete the challenge to earn more points!
            </p>
            {challenges.map((ch) => (
              <ChallengeCard key={ch.id} challenge={ch} slug={slug} />
            ))}
          </div>
        )}

        <Button asChild variant="ghost" className="mt-6">
          <Link to={`/hunt/${slug}`}>
            <ArrowLeftIcon className="mr-2 h-4 w-4" />
            Back to passport
          </Link>
        </Button>
      </div>
    </>
  );
}

function AlreadyCapturedView({
  stationName,
  stationId,
  hunt,
  slug
}: {
  stationName: string;
  stationId: string;
  hunt: HuntState;
  slug: string;
}) {
  const challenges = hunt.challenges.filter((c) => c.stationId === stationId);

  return (
    <div className="mx-auto flex min-h-[100dvh] max-w-md flex-col items-center justify-center p-6">
      <div className="mb-6 flex flex-col items-center text-center">
        <div className="mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-blue-500/10">
          <CheckCircle2Icon className="h-10 w-10 text-blue-600 dark:text-blue-400" />
        </div>
        <h1 className="text-xl font-bold tracking-tight">Already Captured</h1>
        <p className="mt-1 text-muted-foreground">
          You've already captured <span className="font-medium">{stationName}</span>.
        </p>
      </div>

      {challenges.length > 0 && (
        <div className="w-full space-y-3">
          <p className="text-center text-sm text-muted-foreground">
            Challenges for this station:
          </p>
          {challenges.map((ch) => (
            <ChallengeCard key={ch.id} challenge={ch} slug={slug} />
          ))}
        </div>
      )}

      <Button asChild variant="ghost" className="mt-6">
        <Link to={`/hunt/${slug}`}>
          <ArrowLeftIcon className="mr-2 h-4 w-4" />
          Back to passport
        </Link>
      </Button>
    </div>
  );
}

function InvalidCodeView({ slug }: { slug: string }) {
  return (
    <div className="mx-auto flex min-h-[100dvh] max-w-md flex-col items-center justify-center p-6">
      <div className="mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-destructive/10">
        <AlertTriangleIcon className="h-10 w-10 text-destructive" />
      </div>
      <h1 className="text-xl font-bold tracking-tight">Invalid Station Code</h1>
      <p className="mt-1 text-center text-muted-foreground">
        This station code doesn't match any active station. The code may have
        expired or the QR poster may be for a different hunt.
      </p>
      {slug ? (
        <Button asChild variant="ghost" className="mt-6">
          <Link to={`/hunt/${slug}`}>
            <ArrowLeftIcon className="mr-2 h-4 w-4" />
            Go to hunt
          </Link>
        </Button>
      ) : (
        <Button asChild variant="ghost" className="mt-6">
          <Link to="/">
            <ArrowLeftIcon className="mr-2 h-4 w-4" />
            Home
          </Link>
        </Button>
      )}
    </div>
  );
}

function ExpiredSessionView({ slug }: { slug: string }) {
  return (
    <div className="mx-auto flex min-h-[100dvh] max-w-md flex-col items-center justify-center p-6">
      <div className="mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-orange-500/10">
        <RotateCcwIcon className="h-10 w-10 text-orange-600 dark:text-orange-400" />
      </div>
      <h1 className="text-xl font-bold tracking-tight">Session Expired</h1>
      <p className="mt-1 text-center text-muted-foreground">
        Your hunt session has expired. Rejoin the hunt to continue.
      </p>
      <Button asChild className="mt-6">
        <Link to={`/hunt/${slug}`}>Rejoin hunt</Link>
      </Button>
    </div>
  );
}

// ── Main checkin page ─────────────────────────────────────────────────────

export function CheckinPage() {
  const params = useParams();
  const slug = params.huntSlug ?? '';
  const stationCode = params.stationCode ?? '';

  const { session, clearSession } = useHuntSession(slug);
  const huntQuery = useGetHunt(slug);

  const [phase, setPhase] = React.useState<CapturePhase>({ kind: 'idle' });
  const [needsJoin, setNeedsJoin] = React.useState(false);
  const captureAttempted = React.useRef(false);

  // Capture mutation — uses raw captureStation so we can pass the token
  // dynamically (the generated hook captures the request option at creation).
  const captureMutation = useMutation({
    mutationFn: (vars: { slug: string; stationCode: string; token: string }) =>
      captureStation(
        vars.slug,
        { stationCode: vars.stationCode },
        huntAuthHeaders(vars.token)
      ),
    onSuccess: (data) => {
      setPhase({ kind: 'success', result: data });
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        if (err.status === 409 && err.code === 'ALREADY_CAPTURED') {
          const body = err.body as { stationId?: string; stationName?: string } | undefined;
          setPhase({
            kind: 'already',
            stationId: body?.stationId ?? '',
            stationName: body?.stationName ?? 'this station'
          });
        } else if (err.status === 400) {
          setPhase({ kind: 'invalid' });
        } else if (err.status === 401) {
          clearSession();
          setPhase({ kind: 'expired' });
        } else {
          setPhase({ kind: 'error', message: err.message });
        }
      } else {
        setPhase({ kind: 'error', message: 'Something went wrong. Please try again.' });
      }
    }
  });

  // Auto-trigger capture when a session is available and we haven't tried yet.
  React.useEffect(() => {
    if (
      session &&
      !captureAttempted.current &&
      phase.kind === 'idle' &&
      !captureMutation.isPending
    ) {
      captureAttempted.current = true;
      captureMutation.mutate({ slug, stationCode, token: session.token });
    }
  }, [session, phase.kind, captureMutation, slug, stationCode]);

  // ── Loading state ───────────────────────────────────────────────────────
  if (huntQuery.isLoading) {
    return (
      <div className="mx-auto flex min-h-[100dvh] max-w-md flex-col items-center justify-center p-6">
        <Skeleton className="h-32 w-full rounded-xl" />
        <Skeleton className="mt-4 h-8 w-48" />
        <Skeleton className="mt-2 h-4 w-64" />
      </div>
    );
  }

  // ── Hunt not found / service unavailable ────────────────────────────────
  if (huntQuery.isError || !huntQuery.data) {
    const err = huntQuery.error;
    const isNotFound = err instanceof ApiError && err.status === 404;
    return (
      <div className="mx-auto flex min-h-[100dvh] max-w-md flex-col items-center justify-center p-6">
        <div className="mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-destructive/10">
          <AlertTriangleIcon className="h-10 w-10 text-destructive" />
        </div>
        <h1 className="text-xl font-bold tracking-tight">
          {isNotFound ? 'Hunt Not Found' : 'Unavailable'}
        </h1>
        <p className="mt-1 text-center text-muted-foreground">
          {isNotFound
            ? "This hunt doesn't exist or may have been removed."
            : 'The hunt service is temporarily unavailable. Please try again.'}
        </p>
        <Button asChild variant="ghost" className="mt-6">
          <Link to="/">
            <ArrowLeftIcon className="mr-2 h-4 w-4" />
            Home
          </Link>
        </Button>
      </div>
    );
  }

  const hunt = huntQuery.data;

  // ── Draft hunt — not open yet ───────────────────────────────────────────
  if (hunt.status === 'draft') {
    return (
      <div className="mx-auto flex min-h-[100dvh] max-w-md flex-col items-center justify-center p-6">
        <div className="mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-muted">
          <QrCodeIcon className="h-10 w-10 text-muted-foreground" />
        </div>
        <h1 className="text-xl font-bold tracking-tight">Hunt Not Open Yet</h1>
        <p className="mt-1 text-center text-muted-foreground">
          {hunt.name} hasn't started yet. Check back soon!
        </p>
        <Button asChild variant="ghost" className="mt-6">
          <Link to="/">
            <ArrowLeftIcon className="mr-2 h-4 w-4" />
            Home
          </Link>
        </Button>
      </div>
    );
  }

  // ── Closed hunt — redirect to passport for results ──────────────────────
  if (hunt.status === 'closed') {
    return (
      <div className="mx-auto flex min-h-[100dvh] max-w-md flex-col items-center justify-center p-6">
        <div className="mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-blue-500/10">
          <TrophyIcon className="h-10 w-10 text-blue-600 dark:text-blue-400" />
        </div>
        <h1 className="text-xl font-bold tracking-tight">Hunt Closed</h1>
        <p className="mt-1 text-center text-muted-foreground">
          {hunt.name} has ended. Check the final results on the passport.
        </p>
        <Button asChild className="mt-6">
          <Link to={`/hunt/${slug}`}>View results</Link>
        </Button>
      </div>
    );
  }

  // ── Active hunt — need to join first (cold scan) ────────────────────────
  if (!session && !needsJoin) {
    // Stash the station code so the hunt page can redirect back after join
    // if the user navigates away from this page.
    stashPendingCheckin(slug, stationCode);
    setNeedsJoin(true);
  }

  if (!session) {
    return (
      <InlineJoinForm
        slug={slug}
        huntName={hunt.name}
        onJoined={() => {
          // Session is now saved — the useEffect will auto-trigger capture.
          setNeedsJoin(false);
        }}
      />
    );
  }

  // ── Session available — show capture result states ──────────────────────

  if (phase.kind === 'idle' || captureMutation.isPending) {
    return (
      <div className="mx-auto flex min-h-[100dvh] max-w-md flex-col items-center justify-center p-6">
        <div className="flex flex-col items-center gap-3">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-muted border-t-primary" />
          <p className="text-sm text-muted-foreground">Capturing station…</p>
        </div>
      </div>
    );
  }

  if (phase.kind === 'success') {
    return <CaptureCelebration result={phase.result} hunt={hunt} slug={slug} />;
  }

  if (phase.kind === 'already') {
    return (
      <AlreadyCapturedView
        stationName={phase.stationName}
        stationId={phase.stationId}
        hunt={hunt}
        slug={slug}
      />
    );
  }

  if (phase.kind === 'invalid') {
    return <InvalidCodeView slug={slug} />;
  }

  if (phase.kind === 'expired') {
    return <ExpiredSessionView slug={slug} />;
  }

  // Generic error
  return (
    <div className="mx-auto flex min-h-[100dvh] max-w-md flex-col items-center justify-center p-6">
      <div className="mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-destructive/10">
        <AlertTriangleIcon className="h-10 w-10 text-destructive" />
      </div>
      <h1 className="text-xl font-bold tracking-tight">Something Went Wrong</h1>
      <p className="mt-1 text-center text-muted-foreground">{phase.message}</p>
      <Button asChild variant="ghost" className="mt-6">
        <Link to={`/hunt/${slug}`}>
          <ArrowLeftIcon className="mr-2 h-4 w-4" />
          Back to passport
        </Link>
      </Button>
    </div>
  );
}
